// ** React Imports
import { useState, useEffect } from "react";

// ** MUI Components
import { Box, Typography } from "@mui/material";

// ** Icon Imports
import Icon from "../../../components/icon";

// ** Third Party Imports
import dayjs from "dayjs";

// ** API Imports
import {
  GetJobAssetTotalOverview,
  GetJobMaterialTotalOverview,
  GetJobLaborTotalOverview,
  GetJobMiscCostTotalOverview,
  GetJobSubcontractorTotalOverview,
} from "./TotalOverview";

// ** Util Imports
import { formatUSCurrency } from "components/CurrencyFormat";
import { PercentageFormat } from "components/NumberFormats";

export const useTotalOverviewLogic = (requestID, setLoading, toast) => {
  //#region States
  const [open, setOpen] = useState(false);
  const [selectedButton, setSelectedButton] = useState(0);
  const [filteredRows, setFilteredRows] = useState([]);
  const [filteredColumns, setFilteredColumns] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assetTotalCost, setAssetTotalCost] = useState(0);
  const [materials, setMaterials] = useState([]);
  const [materialTotalCost, setMaterialTotalCost] = useState(0);
  const [labors, setLabors] = useState([]);
  const [laborTotalCost, setLaborTotalCost] = useState(0);
  const [miscCosts, setMiscCosts] = useState([]);
  const [miscTotalCost, setMiscTotalCost] = useState(0);
  const [subcontractors, setSubcontractors] = useState([]);
  const [subcontractorTotalCost, setSubcontractorTotalCost] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [totalProfit, setTotalProfit] = useState(0);
  //#endregion States

  //#region Handlers
  //   useEffect(() => {
  //     setFilteredColumns(columnsMaterial);
  //   }, []);

  const fetchTotalOverview = async (requestID) => {
    if (!requestID || requestID < 0) return;

    setLoading(true);
    try {
      const resAsset = await GetJobAssetTotalOverview(requestID);
      const resMaterial = await GetJobMaterialTotalOverview(requestID);
      const resLabor = await GetJobLaborTotalOverview(requestID);
      const resMiscCost = await GetJobMiscCostTotalOverview(requestID);
      const resSubcontractor = await GetJobSubcontractorTotalOverview(
        requestID
      );

      const assetResult = !resAsset?.isError ? resAsset?.result : null;
      const materialResult = !resMaterial?.isError ? resMaterial?.result : null;
      const laborResult = !resLabor?.isError ? resLabor?.result : null;
      const miscCostResult = !resMiscCost?.isError ? resMiscCost?.result : null;
      const subcontractorResult = !resSubcontractor?.isError
        ? resSubcontractor?.result
        : null;

      if (assetResult) setAssets(assetResult);
      if (materialResult) {
        setMaterials(materialResult);
        setFilteredRows(materialResult);
      }
      if (laborResult) setLabors(laborResult);
      if (miscCostResult) setMiscCosts(miscCostResult);
      if (subcontractorResult) setSubcontractors(subcontractorResult);

      calculateTotalCost(
        materialResult,
        laborResult,
        assetResult,
        miscCostResult,
        subcontractorResult
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalCost = (
    material,
    employee,
    asset,
    miscCost,
    subcontractor
  ) => {
    const extractTotalAmount = (data, costKey) => {
      if (!data) return 0;

      if (Array.isArray(data)) {
        return data.reduce((sum, item) => {
          if (!item || typeof item !== "object") return sum;
          const cost = item[costKey];
          const numCost = Number(cost);
          return sum + (isNaN(numCost) ? 0 : numCost);
        }, 0);
      }

      if (typeof data === "object") {
        const cost = data[costKey];
        const numCost = Number(cost);
        return isNaN(numCost) ? 0 : numCost;
      }

      const numValue = Number(data);
      return isNaN(numValue) ? 0 : numValue;
    };

    const materialCost = extractTotalAmount(material, "budgetedTotalCost");
    const employeeCost = extractTotalAmount(employee, "budgetedTotalCost");
    const assetCost = extractTotalAmount(asset, "budgetedTotalCost");
    const miscCostTotal = extractTotalAmount(miscCost, "budgetedCostAmount");
    const subcontractorCost = extractTotalAmount(subcontractor, "budgetedTotalCost");

    const totalCost =
      materialCost +
      employeeCost +
      assetCost +
      miscCostTotal +
      subcontractorCost;

    setMaterialTotalCost(materialCost);
    setAssetTotalCost(assetCost);
    setLaborTotalCost(employeeCost);
    setMiscTotalCost(miscCostTotal);
    setSubcontractorTotalCost(subcontractorCost);
    setTotalCost(totalCost);
  };

  const handleOpenTotalOverview = (requestID) => {
    setFilteredColumns(columnsMaterial);
    setSelectedButton(0);
    fetchTotalOverview(requestID);
    setOpen(true);
  };

  const handleCloseTotalOverview = () => {
    setOpen(false);
    setFilteredColumns(columnsMaterial);
    setSelectedButton(0);
  };

  const handleFilterTypeChange = (buttonIndex, buttonName) => {
    setSelectedButton(buttonIndex);

    const columnMap = {
      Material: columnsMaterial,
      Employee: columnsEmployee,
      Assets: columnsAsset,
      "Other Cost": columnsMiscCost,
      Subcontractor: columnsSubcontractor,
    };

    const dataMap = {
      Material: materials,
      Employee: labors,
      Assets: assets,
      "Other Cost": miscCosts,
      Subcontractor: subcontractors,
    };

    setFilteredColumns(columnMap[buttonName] || columnsMaterial);

    setFilteredRows(dataMap[buttonName] || materials);
  };
  //#endregion Handlers

  //#region Columns Configuration
  const columnsMaterial = [
    {
      flex: 0.1,
      field: "service",
      minWidth: 140,
      headerName: "Service",
      renderCell: ({ row }) => {
        const { service } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{service}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "materialTypeName",
      minWidth: 140,
      headerName: "Material Type",
      renderCell: ({ row }) => {
        const { materialTypeName } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{materialTypeName}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "materialName",
      minWidth: 140,
      headerName: "Material Name",
      renderCell: ({ row }) => {
        const { materialName } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{materialName}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "sku",
      minWidth: 140,
      headerName: "SKU",
      renderCell: ({ row }) => {
        const { sku } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{sku}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "unitName",
      minWidth: 160,
      headerName: "Unit of Measure",
      renderCell: ({ row }) => {
        const { unitName, unitSymbol } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{unitName}{unitSymbol ? ` (${unitSymbol})` : ''}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedQuantity",
      minWidth: 140,
      headerName: "Quantity",
      renderCell: ({ row }) => {
        const { budgetedQuantity } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{budgetedQuantity}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedCostPerUnit",
      minWidth: 140,
      headerName: "Cost / Unit",
      renderCell: ({ row }) => {
        const { budgetedCostPerUnit } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedCostPerUnit || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedTotalCost",
      minWidth: 140,
      headerName: "Total Cost",
      renderCell: ({ row }) => {
        const { budgetedTotalCost } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedTotalCost || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedPricePerUnit",
      minWidth: 140,
      headerName: "Price / Unit",
      renderCell: ({ row }) => {
        const { budgetedPricePerUnit } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedPricePerUnit || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedTotalPrice",
      minWidth: 140,
      headerName: "Total Price",
      renderCell: ({ row }) => {
        const { budgetedTotalPrice } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedTotalPrice || 0)}
            </Typography>
          </Box>
        );
      },
    },
  ];

  const columnsEmployee = [
    {
      flex: 0.1,
      field: "service",
      minWidth: 140,
      headerName: "Service",
      renderCell: ({ row }) => {
        const { service } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{service}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "skillType",
      minWidth: 140,
      headerName: "Skill Type",
      renderCell: ({ row }) => {
        const { skillType } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{skillType}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "employeeName",
      minWidth: 140,
      headerName: "Employee",
      renderCell: ({ row }) => {
        const { employeeName } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{employeeName}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedCostPerHour",
      minWidth: 140,
      headerName: "Cost / Hour",
      renderCell: ({ row }) => {
        const { budgetedCostPerHour } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedCostPerHour || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedHours",
      minWidth: 140,
      headerName: "Budgeted Hours",
      renderCell: ({ row }) => {
        const { budgetedHours } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{budgetedHours}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedTotalCost",
      minWidth: 140,
      headerName: "Total Cost",
      renderCell: ({ row }) => {
        const { budgetedTotalCost } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedTotalCost || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedPricePerHour",
      minWidth: 140,
      headerName: "Price / Hour",
      renderCell: ({ row }) => {
        const { budgetedPricePerHour } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedPricePerHour || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedTotalPrice",
      minWidth: 140,
      headerName: "Total Price",
      renderCell: ({ row }) => {
        const { budgetedTotalPrice } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedTotalPrice || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "markup",
      minWidth: 140,
      headerName: "Markup %",
      renderCell: ({ row }) => {
        const { markup } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {PercentageFormat(markup || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedTotalProfit",
      minWidth: 140,
      headerName: "Profit",
      renderCell: ({ row }) => {
        const { budgetedTotalProfit } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedTotalProfit || 0)}
            </Typography>
          </Box>
        );
      },
    },
  ];

  const columnsAsset = [
    {
      flex: 0.1,
      field: "service",
      minWidth: 140,
      headerName: "Service",
      renderCell: ({ row }) => {
        const { service } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{service}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "assetTypeName",
      minWidth: 140,
      headerName: "Asset Type",
      renderCell: ({ row }) => {
        const { assetTypeName } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{assetTypeName}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "assetName",
      minWidth: 140,
      headerName: "Asset Name",
      renderCell: ({ row }) => {
        const { assetName } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{assetName}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedQuantity",
      minWidth: 140,
      headerName: "Quantity",
      renderCell: ({ row }) => {
        const { budgetedQuantity } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{budgetedQuantity}</Typography>
          </Box>
        );
      },
    },
    // {
    //   flex: 0.1,
    //   field: "hourPerDay",
    //   minWidth: 140,
    //   headerName: "Hour / Day",
    //   renderCell: ({ row }) => {
    //     const { hourPerDay } = row;

    //     return (
    //       <Box
    //         sx={{
    //           display: "flex",
    //           alignItems: "center",
    //           height: "100%",
    //         }}
    //       >
    //         <Typography sx={{ flexGrow: 1 }}>{hourPerDay}</Typography>
    //       </Box>
    //     );
    //   },
    // },
    {
      flex: 0.1,
      field: "budgetedCostPerUnit",
      minWidth: 140,
      headerName: "Cost Per Asset",
      renderCell: ({ row }) => {
        const { budgetedCostPerUnit } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedCostPerUnit || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedDuration",
      minWidth: 140,
      headerName: "Duration",
      renderCell: ({ row }) => {
        const { budgetedDuration } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{budgetedDuration}h</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedTotalCost",
      minWidth: 140,
      headerName: "Total Cost",
      renderCell: ({ row }) => {
        const { budgetedTotalCost } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedTotalCost || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "billToCustomer",
      minWidth: 140,
      headerName: "Bill To Customer",
      renderCell: ({ row }) => {
        const { billToCustomer } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              color: billToCustomer ? "success.main" : "error.main",
            }}
          >
            {billToCustomer ? (
              <Icon icon="tabler:circle-check" />
            ) : (
              <Icon icon="tabler:circle-x" />
            )}
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedPricePerUnit",
      minWidth: 140,
      headerName: "Price Per Asset",
      renderCell: ({ row }) => {
        const { budgetedPricePerUnit } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedPricePerUnit || 0)}
            </Typography>
          </Box>
        );
      },
    },
  ];

  const columnsMiscCost = [
    {
      flex: 0.1,
      field: "service",
      minWidth: 140,
      headerName: "Service",
      renderCell: ({ row }) => {
        const { service } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{service}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "costName",
      minWidth: 140,
      headerName: "Cost Name",
      renderCell: ({ row }) => {
        const { costName } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{costName}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedCostAmount",
      minWidth: 140,
      headerName: "Cost Amount",
      renderCell: ({ row }) => {
        const { budgetedCostAmount } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedCostAmount || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "billToCustomer",
      minWidth: 140,
      headerName: "Bill To Customer",
      renderCell: ({ row }) => {
        const { billToCustomer } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              color: billToCustomer ? "success.main" : "error.main",
            }}
          >
            {billToCustomer ? (
              <Icon icon="tabler:circle-check" />
            ) : (
              <Icon icon="tabler:circle-x" />
            )}
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedCustomerPrice",
      minWidth: 140,
      headerName: "Customer Price",
      renderCell: ({ row }) => {
        const { budgetedCustomerPrice } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedCustomerPrice || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedProfit",
      minWidth: 140,
      headerName: "Total Profit",
      renderCell: ({ row }) => {
        const { budgetedProfit } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedProfit || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedProfitMargin",
      minWidth: 140,
      headerName: "Profit Margin %",
      renderCell: ({ row }) => {
        const { budgetedProfitMargin } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {PercentageFormat(budgetedProfitMargin || 0)}
            </Typography>
          </Box>
        );
      },
    },
  ];

  const columnsSubcontractor = [
    {
      flex: 0.1,
      field: "service",
      minWidth: 140,
      headerName: "Service",
      renderCell: ({ row }) => {
        const { service } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{service}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "skillType",
      minWidth: 140,
      headerName: "Skill Type",
      renderCell: ({ row }) => {
        const { skillType } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{skillType}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "subcontractorName",
      minWidth: 140,
      headerName: "Subcontractor",
      renderCell: ({ row }) => {
        const { subcontractorName } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{subcontractorName}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "unitName",
      minWidth: 160,
      headerName: "Unit of Measure",
      renderCell: ({ row }) => {
        const { unitName, unitSymbol } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{unitName}{unitSymbol ? ` (${unitSymbol})` : ''}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedUnitQuantity",
      minWidth: 140,
      headerName: "Unit Quantity",
      renderCell: ({ row }) => {
        const { budgetedUnitQuantity } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{budgetedUnitQuantity}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedCostPerUnit",
      minWidth: 140,
      headerName: "Cost Per Unit",
      renderCell: ({ row }) => {
        const { budgetedCostPerUnit } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedCostPerUnit || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedTotalCost",
      minWidth: 140,
      headerName: "Total Cost",
      renderCell: ({ row }) => {
        const { budgetedTotalCost } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedTotalCost || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedPricePerUnit",
      minWidth: 140,
      headerName: "Price Per Unit",
      renderCell: ({ row }) => {
        const { budgetedPricePerUnit } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedPricePerUnit || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedTotalPrice",
      minWidth: 140,
      headerName: "Total Price",
      renderCell: ({ row }) => {
        const { budgetedTotalPrice } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>
              {formatUSCurrency(budgetedTotalPrice || 0)}
            </Typography>
          </Box>
        );
      },
    },
  ];
  //#endregion Columns Configuration

  const TotalOverviewProps = {
    open,
    handleOpenTotalOverview,
    handleCloseTotalOverview,
    filteredColumns,
    filteredRows,
    selectedButton,
    handleFilterTypeChange,
    assetTotalCost,
    materialTotalCost,
    laborTotalCost,
    miscTotalCost,
    subcontractorTotalCost,
    totalCost,
  };

  return {
    TotalOverviewProps,
  };
};
